import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Card,
  CardHeader,
  Button,
  Select,
  Badge,
  Pagination,
  LoadingSkeleton,
  Input,
  Modal,
  Textarea,
  StatCard,
  StatsGrid,
} from '@nexora/design-system';
import { useAuth } from '../../shared/auth/AuthContext';
import {
  usePurchaseRequests,
  usePurchaseRequest,
  useCreatePurchaseRequest,
  useSubmitPurchaseRequest,
  useApprovePurchaseRequest,
  useRejectPurchaseRequest,
  useCancelPurchaseRequest,
} from './usePurchaseRequests';
import type { CreatePurchaseRequestInput } from './usePurchaseRequests';
import { formatCurrency, formatDate, formatDateTime } from '../../shared/utils/format';
import {
  requestStatusMeta,
  requestEstimatedTotal,
  summarizeRequests,
  filterRequests,
} from './purchaseRequestView';

/* ── Constantes ── */

const PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  { value: 'ALL', label: 'Todos os status' },
  { value: 'DRAFT', label: 'Rascunho' },
  { value: 'PENDING_APPROVAL', label: 'Aguard. aprovação' },
  { value: 'APPROVED', label: 'Aprovada' },
  { value: 'REJECTED', label: 'Reprovada' },
  { value: 'CONVERTED', label: 'Convertida' },
  { value: 'CANCELLED', label: 'Cancelada' },
];

/* ── Helpers de formato ── */

/* ── Ícones (stroke, 22px) ── */

function iconProps() {
  return {
    width: 22,
    height: 22,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  } as const;
}

function ClipboardIcon(): JSX.Element {
  return (
    <svg {...iconProps()}>
      <rect x="8" y="2" width="8" height="4" rx="1" />
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <path d="M9 12h6M9 16h6" />
    </svg>
  );
}

function HourglassIcon(): JSX.Element {
  return (
    <svg {...iconProps()}>
      <path d="M6 2h12M6 22h12M8 2v4l4 4 4-4V2M8 22v-4l4-4 4 4v4" />
    </svg>
  );
}

function FlameIcon(): JSX.Element {
  return (
    <svg {...iconProps()}>
      <path d="M12 22c4.4 0 7-2.8 7-6.5 0-4-3-6-4.5-9C13 4 12 2 12 2S7 6 6 11c-.6 3 1 5.5 1 5.5" />
      <path d="M12 22c-2 0-3.5-1.2-3.5-3 0-2 2-3 2.5-5 .3 1.5 2.5 2 2.5 4.5 0 2-1 3.5-1.5 3.5Z" />
    </svg>
  );
}

function WalletIcon(): JSX.Element {
  return (
    <svg {...iconProps()}>
      <path d="M20 7H4a2 2 0 0 1 0-4h14v4" />
      <path d="M20 7a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5" />
      <circle cx="17.5" cy="13.5" r="1" />
    </svg>
  );
}

/* ── Página ── */

export function ProcurementRequestsPage(): JSX.Element {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';

  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [detailId, setDetailId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastVariant, setToastVariant] = useState<'success' | 'error'>('success');

  const { data, isLoading, error, refetch } = usePurchaseRequests(tenantId, {
    status: statusFilter,
    page,
    limit: PAGE_SIZE,
  });

  const createMutation = useCreatePurchaseRequest(tenantId);
  const submitMutation = useSubmitPurchaseRequest(tenantId);
  const approveMutation = useApprovePurchaseRequest(tenantId);
  const rejectMutation = useRejectPurchaseRequest(tenantId);
  const cancelMutation = useCancelPurchaseRequest(tenantId);

  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => setToastMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToastMessage(message);
    setToastVariant(variant);
  }, []);

  const items = useMemo(() => data?.items ?? [], [data]);
  const summary = useMemo(() => summarizeRequests(items), [items]);
  const filtered = useMemo(() => filterRequests(items, search), [items, search]);

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleStatusChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
    setPage(1);
  }, []);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  }, []);

  const handleCreate = useCallback(
    async (input: CreatePurchaseRequestInput) => {
      try {
        await createMutation.mutateAsync(input);
        showToast('Solicitação criada como rascunho. Submeta para aprovação na tabela.', 'success');
        setCreateOpen(false);
      } catch (err) {
        showToast(err instanceof Error ? err.message : 'Erro ao criar solicitação.', 'error');
      }
    },
    [createMutation, showToast],
  );

  const handleSubmit = useCallback(
    async (id: string) => {
      try {
        await submitMutation.mutateAsync(id);
        showToast('Solicitação submetida para aprovação.', 'success');
      } catch (err) {
        showToast(err instanceof Error ? err.message : 'Erro ao submeter solicitação.', 'error');
      }
    },
    [submitMutation, showToast],
  );

  const handleApprove = useCallback(
    async (id: string) => {
      try {
        await approveMutation.mutateAsync(id);
        showToast('Solicitação aprovada com sucesso.', 'success');
      } catch (err) {
        showToast(err instanceof Error ? err.message : 'Erro ao aprovar solicitação.', 'error');
      }
    },
    [approveMutation, showToast],
  );

  const handleReject = useCallback(
    async (reason: string) => {
      if (!rejectId) return;
      try {
        await rejectMutation.mutateAsync({ id: rejectId, reason });
        showToast('Solicitação reprovada.', 'success');
        setRejectId(null);
      } catch (err) {
        showToast(err instanceof Error ? err.message : 'Erro ao reprovar solicitação.', 'error');
      }
    },
    [rejectMutation, rejectId, showToast],
  );

  const handleCancel = useCallback(
    async (id: string) => {
      try {
        await cancelMutation.mutateAsync(id);
        showToast('Solicitação cancelada.', 'success');
      } catch (err) {
        showToast(err instanceof Error ? err.message : 'Erro ao cancelar solicitação.', 'error');
      }
    },
    [cancelMutation, showToast],
  );

  const mutating =
    submitMutation.isPending ||
    approveMutation.isPending ||
    rejectMutation.isPending ||
    cancelMutation.isPending;

  return (
    <div className="pr-page">
      {/* Header executivo */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <p className="pr-page__eyebrow">Compras · Solicitações</p>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">
            Solicitações de Compra
            {data && <span className="font-body-md text-body-md text-on-surface-variant font-normal ml-sm">({total})</span>}
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Fluxo de solicitação: rascunho, aprovação e conversão em ordem de compra.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-sm">
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            + Nova solicitação
          </Button>
        </div>
      </div>

      {/* KPIs derivados da lista (agregação client-side, sem refetch) */}
      {isLoading ? (
        <StatsGrid>
          <LoadingSkeleton width="100%" height="110px" />
          <LoadingSkeleton width="100%" height="110px" />
          <LoadingSkeleton width="100%" height="110px" />
          <LoadingSkeleton width="100%" height="110px" />
        </StatsGrid>
      ) : (
        <StatsGrid>
          <StatCard
            variant="primary"
            icon={<ClipboardIcon />}
            value={String(total)}
            label="Solicitações (total servidor)"
            trend={{ value: `${summary.count} nesta página`, direction: 'neutral' }}
          />
          <StatCard
            variant="info"
            icon={<HourglassIcon />}
            value={String(summary.pendingApproval)}
            label="Aguard. aprovação"
            trend={{ value: 'Página atual · derivado', direction: 'neutral' }}
          />
          <StatCard
            variant="warning"
            icon={<FlameIcon />}
            value={String(summary.urgent)}
            label="Urgentes"
            trend={{ value: 'Página atual · derivado', direction: summary.urgent > 0 ? 'down' : 'up' }}
          />
          <StatCard
            variant="success"
            icon={<WalletIcon />}
            value={formatCurrency(summary.estimatedTotal)}
            label="Valor estimado"
            trend={{ value: 'Página atual · derivado', direction: 'neutral' }}
          />
        </StatsGrid>
      )}

      {/* Filtros */}
      <Card padding="1rem" variant="outlined">
        <div className="pr-page__filter-row">
          <div className="pr-page__filter-field">
            <Select
              label="Filtrar por status"
              value={statusFilter}
              onChange={handleStatusChange}
              aria-label="Filtrar por status (aplica filtro no servidor)"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </div>
          <div className="pr-page__filter-field pr-page__filter-field--grow">
            <Input
              label="Buscar na página"
              placeholder="Número, justificativa ou solicitante..."
              value={search}
              onChange={handleSearchChange}
              aria-label="Buscar na página atual por número, justificativa ou solicitante"
            />
          </div>
        </div>
      </Card>

      {/* Conteúdo */}
      <Card variant="outlined" padding="0">
        <CardHeader
          className="px-md pt-md"
          action={
            <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
              {total ?? 0} {(total ?? 0) === 1 ? 'solicitação' : 'solicitações'}
            </span>
          }
        >
          <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Solicitações</h3>
        </CardHeader>
        {isLoading && (
          <div className="pr-page__loading" aria-busy="true" aria-live="polite">
            <LoadingSkeleton width="100%" height="48px" />
            <LoadingSkeleton width="100%" height="48px" />
            <LoadingSkeleton width="100%" height="48px" />
            <span className="pr-page__loading-text">Carregando solicitações...</span>
          </div>
        )}

        {error && !isLoading && (
          <div className="pr-page__error" role="alert">
            <p className="pr-page__error-title">Erro ao carregar solicitações de compra</p>
            <p className="pr-page__error-detail">{(error as Error).message}</p>
            <Button
              variant="secondary"
              onClick={() => {
                void refetch();
              }}
            >
              Tentar novamente
            </Button>
          </div>
        )}

        {!isLoading && !error && filtered.length === 0 && (
          <div className="pr-page__empty" role="status">
            <p className="pr-page__empty-text">
              {search
                ? 'Nenhuma solicitação nesta página corresponde à busca.'
                : 'Nenhuma solicitação de compra encontrada.'}
            </p>
            {!search && (
              <Button variant="outline" size="sm" onClick={() => setCreateOpen(true)}>
                Criar primeira solicitação
              </Button>
            )}
          </div>
        )}

        {!isLoading && !error && filtered.length > 0 && (
          <div className="pr-page__table-wrap">
            <table className="pr-page__table">
              <thead>
                <tr>
                  <th scope="col">Número</th>
                  <th scope="col">Justificativa</th>
                  <th scope="col">Solicitante</th>
                  <th scope="col">Urgente</th>
                  <th scope="col" className="right">Valor estimado</th>
                  <th scope="col">Status</th>
                  <th scope="col">Criada em</th>
                  <th scope="col">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const meta = requestStatusMeta(r.status);
                  return (
                    <tr key={r.id} className="pr-row">
                      <td className="pr-cell--number">{r.requestNumber}</td>
                      <td className="pr-cell--justification" title={r.justification}>
                        {r.justification}
                      </td>
                      <td>{r.requestedBy}</td>
                      <td>
                        {r.urgent ? (
                          <Badge variant="warning" size="sm" dot>
                            Urgente
                          </Badge>
                        ) : (
                          <span className="pr-cell--muted">—</span>
                        )}
                      </td>
                      <td className="pr-cell--total right">{formatCurrency(requestEstimatedTotal(r))}</td>
                      <td>
                        <Badge variant={meta.variant} size="sm" dot>
                          {meta.label}
                        </Badge>
                      </td>
                      <td className="pr-cell--date">{formatDate(r.createdAt)}</td>
                      <td className="pr-cell--actions">
                        <div className="pr-actions">
                          <Button variant="ghost" size="sm" onClick={() => setDetailId(r.id)}>
                            Detalhes
                          </Button>
                          {r.status === 'DRAFT' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              loading={submitMutation.isPending}
                              disabled={mutating}
                              onClick={() => handleSubmit(r.id)}
                            >
                              Submeter
                            </Button>
                          )}
                          {r.status === 'PENDING_APPROVAL' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                loading={approveMutation.isPending}
                                disabled={mutating}
                                onClick={() => handleApprove(r.id)}
                              >
                                Aprovar
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={mutating}
                                onClick={() => setRejectId(r.id)}
                                className="pr-btn--danger"
                              >
                                Reprovar
                              </Button>
                            </>
                          )}
                          {(r.status === 'DRAFT' || r.status === 'PENDING_APPROVAL') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              loading={cancelMutation.isPending}
                              disabled={mutating}
                              onClick={() => handleCancel(r.id)}
                              className="pr-btn--danger"
                            >
                              Cancelar
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!isLoading && !error && (
          <div className="pr-page__footer">
            <span className="pr-page__footer-text">
              {search
                ? `Mostrando ${filtered.length} de ${items.length} (página atual · busca client-side)`
                : `Mostrando ${items.length} de ${total} · página ${page} de ${totalPages}`}
            </span>
            {totalPages > 1 && (
              <Pagination page={page} totalPages={totalPages} onChange={(p) => setPage(p)} />
            )}
          </div>
        )}
      </Card>

      {/* Drawer somente-leitura (detalhe real GET :id) */}
      <RequestDetailDrawer
        tenantId={tenantId}
        requestId={detailId}
        onClose={() => setDetailId(null)}
      />

      {/* Modal criar */}
      <CreateRequestModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
        submitting={createMutation.isPending}
      />

      {/* Modal reprovar com justificativa */}
      <RejectRequestModal
        isOpen={rejectId !== null}
        onClose={() => setRejectId(null)}
        onSubmit={handleReject}
        submitting={rejectMutation.isPending}
      />

      {/* Toast */}
      {toastMessage && (
        <div role="alert" className={`pr-toast pr-toast--${toastVariant}`}>
          {toastMessage}
          <button onClick={() => setToastMessage(null)} aria-label="Fechar notificação" className="pr-toast__close">
            &times;
          </button>
        </div>
      )}

      <style>{`
        .pr-page { padding: 1.5rem; display: flex; flex-direction: column; gap: 1rem; }
        .pr-page__eyebrow { margin: 0 0 0.25rem; font-size: 0.75rem; letter-spacing: 0.08em; text-transform: uppercase; color: #002045; font-weight: 700; }
        .pr-page__filter-row { display: flex; align-items: flex-end; gap: 1rem; flex-wrap: wrap; }
        .pr-page__filter-field { min-width: 220px; }
        .pr-page__filter-field--grow { flex: 1; min-width: 260px; }
        .pr-page__loading { display: flex; flex-direction: column; gap: 0.5rem; padding: 1.5rem; color: #43474e; }
        .pr-page__loading-text { font-size: 0.875rem; }
        .pr-page__error { padding: 3rem 2rem; text-align: center; color: #ba1a1a; display: flex; flex-direction: column; align-items: center; gap: 0.5rem; }
        .pr-page__error-title { font-weight: 600; margin: 0; }
        .pr-page__error-detail { font-size: 0.875rem; margin: 0; color: #74777f; }
        .pr-page__empty { padding: 3rem 2rem; text-align: center; color: #74777f; }
        .pr-page__empty-text { margin: 0 0 1rem; font-size: 0.875rem; }
        .pr-page__table-wrap { overflow-x: auto; }
        .pr-page__table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .pr-page__table th { text-align: left; padding: 0.75rem 1rem; background: #f2f4f6; color: #43474e; font-weight: 600; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid #e0e3e5; white-space: nowrap; position: sticky; top: 0; z-index: 1;}
        .pr-page__table th.right, .pr-page__table td.right { text-align: right; }
        .pr-page__table td { padding: 0.75rem 1rem; border-bottom: 1px solid #eceef0; vertical-align: middle; }
        .pr-row:hover td { background: #eceef0; }
        .pr-cell--number { font-weight: 600; white-space: nowrap; font-variant-numeric: tabular-nums; font-family: "JetBrains Mono", ui-monospace, monospace;}
        .pr-cell--justification { max-width: 280px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .pr-cell--total { font-weight: 600; white-space: nowrap; }
        .pr-cell--date { white-space: nowrap; }
        .pr-cell--muted { color: #c4c6cf; }
        .pr-cell--actions { white-space: nowrap; }
        .pr-actions { display: flex; gap: 0.25rem; align-items: center; flex-wrap: wrap; }
        .pr-btn--danger { color: #ba1a1a; }
        .pr-page__footer { display: flex; align-items: center; justify-content: space-between; gap: 1rem; flex-wrap: wrap; padding: 1rem; border-top: 1px solid #eceef0; }
        .pr-page__footer-text { font-size: 0.8125rem; color: #43474e; }
        .pr-toast { position: fixed; bottom: 1.5rem; right: 1.5rem; padding: 0.75rem 1rem; border-radius: 8px; font-size: 0.875rem; font-weight: 500; box-shadow: 0 4px 12px rgba(0,0,0,0.1); z-index: 10000; max-width: 420px; }
        .pr-toast--success { background: #d3f5d6; border: 1px solid #b6ebbb; color: #2e7d32; }
        .pr-toast--error { background: #ffdad6; border: 1px solid #ffb4ab; color: #ba1a1a; }
        .pr-toast__close { margin-left: 0.75rem; background: none; border: none; cursor: pointer; color: inherit; font-size: 1rem; padding: 0; line-height: 1; }
        .pr-detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; margin-bottom: 1rem; }
        .pr-detail-label { margin: 0; font-size: 0.75rem; color: #74777f; }
        .pr-detail-value { margin: 0.125rem 0 0; font-weight: 600; font-size: 0.875rem; word-break: break-word; }
        .pr-detail-table { width: 100%; border-collapse: collapse; font-size: 0.8125rem; }
        .pr-detail-table th { text-align: left; padding: 0.5rem 0.75rem; background: #f2f4f6; color: #43474e; font-weight: 600; font-size: 0.75rem; border-bottom: 2px solid #e0e3e5; position: sticky; top: 0; z-index: 1;}
        .pr-detail-table th.right, .pr-detail-table td.right { text-align: right; }
        .pr-detail-table td { padding: 0.5rem 0.75rem; border-bottom: 1px solid #eceef0; }
        .pr-section-title { margin: 1rem 0 0.5rem; font-size: 1.25rem; line-height: 1.75rem; font-weight: 600; color: #191c1e; }
        .pr-form { display: flex; flex-direction: column; gap: 1rem; }
        .pr-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; }
        .pr-checkbox-row { display: flex; align-items: center; gap: 0.5rem; font-size: 0.875rem; color: #191c1e; }
        .pr-items-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.5rem; }
        .pr-items-title { font-size: 0.875rem; font-weight: 600; color: #191c1e; }
        .pr-item-card { border: 1px solid #e0e3e5; border-radius: 8px; padding: 0.75rem; display: flex; flex-direction: column; gap: 0.75rem; background: #f2f4f6; }
        .pr-form-error { margin: 0; font-size: 0.8125rem; color: #ba1a1a; }
        @media (max-width: 640px) {
          .pr-page { padding: 1rem; }
          .pr-detail-grid, .pr-form-row { grid-template-columns: 1fr; }
          .pr-cell--justification { max-width: 160px; }
        }
        @media (prefers-reduced-motion: reduce) {
          .pr-row td, .pr-actions, .pr-toast { transition: none; }
        }
      `}</style>
    </div>
  );
}

/* ── Drawer somente-leitura ── */

function RequestDetailDrawer({
  tenantId,
  requestId,
  onClose,
}: {
  tenantId: string;
  requestId: string | null;
  onClose: () => void;
}): JSX.Element {
  const { data: request, isLoading, error } = usePurchaseRequest(tenantId, requestId ?? '');

  const footer = (
    <Button variant="outline" onClick={onClose}>
      Fechar
    </Button>
  );

  const historyEntries: Array<{ label: string; value: string }> = [];
  if (request?.approvedBy) historyEntries.push({ label: 'Aprovada por', value: request.approvedBy });
  if (request?.rejectedById)
    historyEntries.push({
      label: 'Reprovada por (id)',
      value: `${request.rejectedById}${request.rejectionReason ? ` — ${request.rejectionReason}` : ''}`,
    });
  else if (request?.rejectionReason) historyEntries.push({ label: 'Motivo da reprovação', value: request.rejectionReason });
  if (request?.convertedAt) historyEntries.push({ label: 'Convertida em', value: formatDateTime(request.convertedAt) });

  return (
    <Modal
      isOpen={requestId !== null}
      onClose={onClose}
      title={request ? `Solicitação ${request.requestNumber}` : 'Detalhes da solicitação'}
      footer={footer}
      size="lg"
    >
      {isLoading && (
        <div className="pr-page__loading" aria-busy="true" aria-live="polite">
          <LoadingSkeleton width="100%" height="48px" />
          <LoadingSkeleton width="100%" height="120px" />
          <span className="pr-page__loading-text">Carregando detalhes...</span>
        </div>
      )}
      {error && !isLoading && (
        <p role="alert" style={{ color: '#ba1a1a', fontSize: '0.875rem' }}>
          {(error as Error).message}
        </p>
      )}
      {request && !isLoading && (
        <div role="document" aria-label={`Detalhes da solicitação ${request.requestNumber}`}>
          <div className="pr-detail-grid">
            <div>
              <p className="pr-detail-label">Status</p>
              <p className="pr-detail-value">
                <Badge variant={requestStatusMeta(request.status).variant} size="sm" dot>
                  {requestStatusMeta(request.status).label}
                </Badge>
              </p>
            </div>
            <div>
              <p className="pr-detail-label">Urgente</p>
              <p className="pr-detail-value">{request.urgent ? 'Sim' : 'Não'}</p>
            </div>
            <div>
              <p className="pr-detail-label">Solicitante</p>
              <p className="pr-detail-value">{request.requestedBy}</p>
            </div>
            <div>
              <p className="pr-detail-label">Criada em</p>
              <p className="pr-detail-value">{formatDateTime(request.createdAt)}</p>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <p className="pr-detail-label">Justificativa</p>
              <p className="pr-detail-value">{request.justification}</p>
            </div>
            <div>
              <p className="pr-detail-label">Valor estimado total</p>
              <p className="pr-detail-value">{formatCurrency(requestEstimatedTotal(request))}</p>
            </div>
            <div>
              <p className="pr-detail-label">Atualizada em</p>
              <p className="pr-detail-value">{formatDateTime(request.updatedAt)}</p>
            </div>
          </div>

          <h3 className="pr-section-title">Itens ({request.items.length})</h3>
          {request.items.length === 0 ? (
            <p style={{ fontSize: '0.875rem', color: '#74777f' }}>Sem itens nesta solicitação.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="pr-detail-table">
                <thead>
                  <tr>
                    <th scope="col">Produto</th>
                    <th scope="col" className="right">Qtd</th>
                    <th scope="col">UM</th>
                    <th scope="col" className="right">Custo est.</th>
                    <th scope="col" className="right">Total est.</th>
                    <th scope="col">Necessário em</th>
                  </tr>
                </thead>
                <tbody>
                  {request.items.map((item) => (
                    <tr key={item.id}>
                      <td style={{ fontWeight: 500 }}>{item.productName}</td>
                      <td className="right">{item.quantity}</td>
                      <td>{item.unitOfMeasure}</td>
                      <td className="right">{formatCurrency(item.estimatedUnitCost)}</td>
                      <td className="right" style={{ fontWeight: 600 }}>
                        {formatCurrency(item.estimatedTotalCost)}
                      </td>
                      <td>{formatDate(item.requiredDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {historyEntries.length > 0 && (
            <>
              <h3 className="pr-section-title">Histórico</h3>
              <div className="pr-detail-grid">
                {historyEntries.map((h) => (
                  <div key={h.label}>
                    <p className="pr-detail-label">{h.label}</p>
                    <p className="pr-detail-value">{h.value}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}

/* ── Modal criar (padrão PurchaseOrdersPage) ── */

interface RequestLineItem {
  productId: string;
  quantity: string;
  estimatedUnitCost: string;
  requiredDate: string;
  justification: string;
}

function CreateRequestModal({
  isOpen,
  onClose,
  onSubmit,
  submitting,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (input: CreatePurchaseRequestInput) => Promise<void>;
  submitting: boolean;
}): JSX.Element {
  const [justification, setJustification] = useState('');
  const [urgent, setUrgent] = useState(false);
  const [departmentId, setDepartmentId] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [items, setItems] = useState<RequestLineItem[]>([
    { productId: '', quantity: '', estimatedUnitCost: '', requiredDate: '', justification: '' },
  ]);

  const reset = useCallback(() => {
    setJustification('');
    setUrgent(false);
    setDepartmentId('');
    setFormError(null);
    setItems([{ productId: '', quantity: '', estimatedUnitCost: '', requiredDate: '', justification: '' }]);
  }, []);

  const updateItem = useCallback((idx: number, field: keyof RequestLineItem, value: string) => {
    setItems((prev) => prev.map((item, i) => (i === idx ? { ...item, [field]: value } : item)));
  }, []);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setFormError(null);
    if (!justification.trim()) {
      setFormError('Informe a justificativa geral da solicitação.');
      return;
    }
    const parsed = items
      .map((item) => ({
        productId: item.productId.trim(),
        quantity: parseFloat(item.quantity) || 0,
        estimatedUnitCost: parseFloat(item.estimatedUnitCost) || 0,
        requiredDate: item.requiredDate,
        justification: item.justification.trim(),
      }))
      .filter((item) => item.productId || item.quantity > 0 || item.justification);
    if (parsed.length === 0) {
      setFormError('Adicione ao menos um item com produto, quantidade e justificativa.');
      return;
    }
    const invalid = parsed.find(
      (item) => !item.productId || item.quantity <= 0 || !item.requiredDate || !item.justification,
    );
    if (invalid) {
      setFormError('Cada item precisa de produto, quantidade > 0, data necessária e justificativa.');
      return;
    }
    await onSubmit({
      justification: justification.trim(),
      urgent,
      ...(departmentId.trim() ? { departmentId: departmentId.trim() } : {}),
      items: parsed,
    });
    reset();
  };

  const valid = justification.trim().length > 0 && items.some((i) => i.productId.trim());

  const footer = (
    <>
      <Button
        variant="outline"
        type="button"
        onClick={() => {
          reset();
          onClose();
        }}
      >
        Cancelar
      </Button>
      <Button variant="primary" type="submit" form="create-pr-form" loading={submitting} disabled={!valid}>
        Criar rascunho
      </Button>
    </>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Nova Solicitação de Compra" footer={footer} size="lg">
      <form id="create-pr-form" onSubmit={(e) => void handleSubmit(e)}>
        <div className="pr-form">
          <Textarea
            label="Justificativa geral"
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            required
            rows={3}
            placeholder="Descreva a necessidade da compra..."
          />
          <div className="pr-form-row">
            <Input
              label="Departamento (opcional)"
              value={departmentId}
              onChange={(e) => setDepartmentId(e.target.value)}
              placeholder="ID do departamento"
            />
            <label className="pr-checkbox-row">
              <input type="checkbox" checked={urgent} onChange={(e) => setUrgent(e.target.checked)} />
              Solicitação urgente
            </label>
          </div>

          <div>
            <div className="pr-items-header">
              <span className="pr-items-title">Itens</span>
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={() =>
                  setItems((prev) => [
                    ...prev,
                    { productId: '', quantity: '', estimatedUnitCost: '', requiredDate: '', justification: '' },
                  ])
                }
              >
                + Adicionar item
              </Button>
            </div>
            {items.map((item, idx) => (
              <div className="pr-item-card" key={idx}>
                <div className="pr-form-row">
                  <Input
                    label="ID do produto"
                    value={item.productId}
                    onChange={(e) => updateItem(idx, 'productId', e.target.value)}
                    placeholder="UUID do produto"
                  />
                  <Input
                    label="Quantidade"
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.quantity}
                    onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div className="pr-form-row">
                  <Input
                    label="Custo unit. estimado (R$)"
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.estimatedUnitCost}
                    onChange={(e) => updateItem(idx, 'estimatedUnitCost', e.target.value)}
                    placeholder="0,00"
                  />
                  <Input
                    label="Necessário em"
                    type="date"
                    value={item.requiredDate}
                    onChange={(e) => updateItem(idx, 'requiredDate', e.target.value)}
                  />
                </div>
                <Input
                  label="Justificativa do item"
                  value={item.justification}
                  onChange={(e) => updateItem(idx, 'justification', e.target.value)}
                  placeholder="Por que este item é necessário?"
                />
                {items.length > 1 && (
                  <div>
                    <Button
                      variant="ghost"
                      size="sm"
                      type="button"
                      className="pr-btn--danger"
                      onClick={() => setItems((prev) => prev.filter((_, i) => i !== idx))}
                      aria-label={`Remover item ${idx + 1}`}
                    >
                      Remover item
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {formError && (
            <p className="pr-form-error" role="alert">
              {formError}
            </p>
          )}
        </div>
      </form>
    </Modal>
  );
}

/* ── Modal reprovar com justificativa ── */

function RejectRequestModal({
  isOpen,
  onClose,
  onSubmit,
  submitting,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void>;
  submitting: boolean;
}): JSX.Element {
  const [reason, setReason] = useState('');

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!reason.trim()) return;
    await onSubmit(reason.trim());
    setReason('');
  };

  const footer = (
    <>
      <Button
        variant="outline"
        type="button"
        onClick={() => {
          setReason('');
          onClose();
        }}
      >
        Cancelar
      </Button>
      <Button
        variant="primary"
        type="submit"
        form="reject-pr-form"
        loading={submitting}
        disabled={!reason.trim()}
      >
        Confirmar reprovação
      </Button>
    </>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Reprovar solicitação" footer={footer}>
      <form id="reject-pr-form" onSubmit={(e) => void handleSubmit(e)}>
        <Textarea
          label="Motivo da reprovação (obrigatório — exigido pelo contrato POST :id/reject)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          required
          rows={4}
          placeholder="Descreva o motivo da reprovação..."
        />
      </form>
    </Modal>
  );
}
